import React from "react";
import {
  HashRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import i18n from "../config/i18n";
import { getCurrentLanguageFromUrl } from '../utils/languageUtils';
import { NavigationEventCatcher } from "../utils/useStopVideoOnNavigation";

// Type for supported language codes

// Import your pages
import Layout from "../components/layout/Layout";
import NotFound from "./NotFound";
import ComicDetail from "./comic/ComicDetail";
import ComicReader from "./comic/ComicReader";
import ComicPage from "./comics";
import GameDetail from "./game/GameDetail";
import GamesPage from "./games";
import HistoryContentsPage from "./history/HistoryContentsPage";
import HomePage from "./home";
import ListPage from "./list";
import CategoryPage from "./movie/CategoryPage";
import MovieDetails from "./movie/MovieDetails";
import MoviesPage from "./movies";
import NovelDetail from "./novel/NovelDetail";
import SavedContentsPage from "./saved/SavedContentsPage";
 import LoginPage from "./user/login";
import RegisterPage from "./user/register";
import FinderPage from "./finder";

// Helper to get detail component by post type
import { SUPPORTED_LANGUAGES } from "../config/supportedLanguages";
import NovelReader from "./novel/NovelReader";
import SearchPage from "./search";

// Type for supported language codes

// Language-aware route wrapper
const LanguageRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const lang = getCurrentLanguageFromUrl();

  // Validate language or redirect to default
  if (!Object.keys(SUPPORTED_LANGUAGES).includes(lang)) {
    // Redirect to fallback language if no valid language in URL
    return <Navigate to={`/${i18n.options.fallbackLng}/`} replace />;
  }

  return <>{element}</>;
};

const AppRouter: React.FC = () => {
    const lang = getCurrentLanguageFromUrl();
   return (
    <HashRouter>
      {/* Component to catch navigation events and stop videos */}
      <Routes>
        {/* Root path now renders HomePage directly */}
        <Route path="/" element={<><NavigationEventCatcher /><Layout /></>}>
          <Route index element={<HomePage />} />
        </Route>

        {/* Main pages wrapped in common Layout (BottomNavigation mounts once) */}
        <Route element={<><NavigationEventCatcher /><Layout /></>}>
          <Route path=":lang">
            <Route index element={<LanguageRoute element={<HomePage />} />} />
            <Route
              path="movies"
              element={<LanguageRoute element={<MoviesPage />} />}
            />
            <Route
              path="comics"
              element={<LanguageRoute element={<ComicPage />} />}
            />
            <Route
              path="games"
              element={<LanguageRoute element={<GamesPage />} />}
            />
            <Route
              path="list"
              element={<LanguageRoute element={<ListPage />} />}
            />
            {/* Finder page route */}
            <Route
              path="finder"
              element={<LanguageRoute element={<FinderPage />} />}
            />
          </Route>
        </Route>

        {/* Detail, auth, search, and fallback routes (no bottom nav) */}
        <Route
          path=":lang/movie/:slug/"
          element={<LanguageRoute element={<MovieDetails />} />}
        />
        <Route
          path=":lang/movie/category/:slug/"
          element={<LanguageRoute element={<CategoryPage />} />}
        />
        <Route
          path=":lang/comic/:slug/"
          element={<LanguageRoute element={<ComicDetail />} />}
        />
        <Route
          path=":lang/comic/category/:slug/"
          element={<LanguageRoute element={<CategoryPage />} />}
        />
        <Route
          path=":lang/novel/:slug/"
          element={<LanguageRoute element={<NovelDetail />} />}
        />
        <Route
          path=":lang/game/:slug/"
          element={<LanguageRoute element={<GameDetail />} />}
        />
        <Route
          path=":lang/game/category/:slug/"
          element={<LanguageRoute element={<CategoryPage />} />}
        />
        <Route
          path=":lang/comic/:slug/:chapterIndex/"
          element={<LanguageRoute element={<ComicReader />} />}
        />
        <Route
          path=":lang/novel/:slug/:chapterIndex/"
          element={<LanguageRoute element={<NovelReader />} />}
        />
        <Route
          path=":lang/user/login/"
          element={<LanguageRoute element={<LoginPage />} />}
        />
        <Route
          path=":lang/user/register/"
          element={<LanguageRoute element={<RegisterPage />} />}
        />
        <Route
          path=":lang/search/"
          element={<LanguageRoute element={<SearchPage />} />}
        />
        
        <Route
          path=":lang/saved"
          element={<LanguageRoute element={<SavedContentsPage />} />}
        />
        <Route
          path=":lang/history"
          element={<LanguageRoute element={<HistoryContentsPage />} />}
        />
        <Route
          path=":lang/*"
          element={<LanguageRoute element={<NotFound />} />}
        />

        {/* Catch any other route and redirect to default language */}
        <Route path="*" element={<Navigate to={`/${lang}/`} replace />} />
      </Routes>
    </HashRouter>
  );
};

export default AppRouter;
